import type { AspectRatio } from '@lumen5/luminary';

import type {
    DashboardVideo,
    EstimatedStatusEnum,
    NarrativeThreadEnum,
    ProjectColorCode,
    ProjectIcon,
    VideoStatus,
} from 'js/src/client';
import { VideoSortBy } from 'js/src/client';
import type { StoreORMInterface } from 'js/src/data/store-orm';
import StoreORM from 'js/src/data/store-orm';

export const VIDEOS_ORM_PREFIX = 'videos';
export const VIEWS_ORM_PREFIX = 'views';

/* ACTION TYPES */
export const DASHBOARD_ACTIONS = {
    SET_ACTIVE_VIEW: 'SET_ACTIVE_VIEW',
    STORE_VIDEOS: 'STORE_VIDEOS',
    ADD_VIEWS: 'ADD_VIEWS',
    REMOVE_VIEW: 'REMOVE_VIEW',
    UPDATE_VIEW_METADATA: 'UPDATE_VIEW_METADATA',
    STORE_LOADED_PAGE_NUMBER: 'STORE_LOADED_PAGE_NUMBER',
    ENABLE_LUMINARY_PREVIEWS: 'ENABLE_LUMINARY_PREVIEWS',
    DISABLE_LUMINARY_PREVIEWS: 'DISABLE_LUMINARY_PREVIEWS',
    STORE_MORE_VIDEOS_TO_LOAD: 'STORE_MORE_VIDEOS_TO_LOAD',
    STORE_RENDER_ESTIMATES: 'STORE_RENDER_ESTIMATES',
    UPDATE_VIDEO_PROPERTY: 'UPDATE_VIDEO_PROPERTY',
    DELETE_VIDEO_FROM_VIEW: 'DELETE_VIDEO_FROM_VIEW',
    DELETE_VIDEO: 'DELETE_VIDEO',
    SET_LOADING_DASHBOARD: 'SET_LOADING_DASHBOARD',
    SET_BULK_ACTION_SPINNER: 'SET_BULK_ACTION_SPINNER',
    STORE_VIDEOS_SORT_BY: 'STORE_VIDEOS_SORT_BY',
    STORE_VIDEOS_FILTER_BY_USER_ID: 'STORE_VIDEOS_FILTER_BY_USER_ID',
    CLEAR_VIDEOS: 'CLEAR_VIDEOS',
    SET_VIDEO_SEARCH_TERM: 'SET_VIDEO_SEARCH_TERM',
    STORE_VIDEOS_FILTER_BY_NARRATIVE_THREAD: 'STORE_VIDEOS_FILTER_BY_NARRATIVE_THREAD',
    STORE_VIDEOS_FILTER_BY_ASPECT_RATIO: 'STORE_VIDEOS_FILTER_BY_ASPECT_RATIO',
    STORE_VIDEOS_FILTER_BY_STATUS: 'STORE_VIDEOS_FILTER_BY_STATUS',
    STORE_VIDEOS_FILTER_BY_TEMPLATE_ID: 'STORE_VIDEOS_FILTER_BY_TEMPLATE_ID',
};

export const VIDEOS_KEY = 'videos';

// we have to use negative ids here, to avoid id collisions with projects,
// which can have arbitrary integers for ids
export const ALL_VIDEOS_VIEW_ID = -1;
export const TRASH_VIDEOS_VIEW_ID = -2;
export const NON_PROJECT_VIEW_IDS = [ALL_VIDEOS_VIEW_ID, TRASH_VIDEOS_VIEW_ID];

export const DEFAULT_VIDEOS_SORT_OPTION = VideoSortBy.LAST_UPDATED_DATE;

export type View = {
    id: number;
    name: string;
    subTitle?: string;
    icon?: ProjectIcon;
    color?: ProjectColorCode;
    moreVideosToLoad: boolean;
    workspaceId?: number;
    loadedUpToPage: number;
    videosSortBy: VideoSortBy;
    filterByUserId?: number;
    filterByNarrativeThread?: NarrativeThreadEnum;
    filterByAspectRatio?: AspectRatio;
    filterByStatus?: VideoStatus;
    filterByTemplateId?: string;
    [VIDEOS_KEY]: StoreORMInterface<DashboardVideo>;
};

export type RenderEstimateStatus = {
    videoId: number;
    estimated_status: EstimatedStatusEnum;
    estimated_time_remaining?: number;
};

export const INITIAL_VIEWS: Array<View> = [
    {
        id: ALL_VIDEOS_VIEW_ID,
        name: 'All videos',
        subTitle: undefined,
        icon: undefined,
        color: undefined,
        moreVideosToLoad: true,
        loadedUpToPage: 0,
        videosSortBy: DEFAULT_VIDEOS_SORT_OPTION,
        [VIDEOS_KEY]: StoreORM.newFromIdPrefix<DashboardVideo>('all-videos').data,
    },
    {
        id: TRASH_VIDEOS_VIEW_ID,
        name: 'Trash',
        subTitle: 'All of your deleted videos stay in trash for 30 days.',
        icon: undefined,
        color: undefined,
        moreVideosToLoad: true,
        loadedUpToPage: 0,
        videosSortBy: DEFAULT_VIDEOS_SORT_OPTION,
        [VIDEOS_KEY]: StoreORM.newFromIdPrefix<DashboardVideo>('trash-videos').data,
    },
];

export function viewIdIsProject(viewId: number) {
    for (let i = 0; i < INITIAL_VIEWS.length; i++) {
        if (viewId === INITIAL_VIEWS[i].id) {
            return false;
        }
    }
    return true;
}

export function viewIdIsProjectOrAllVideos(viewId: number) {
    return viewIdIsProject(viewId) || viewId === ALL_VIDEOS_VIEW_ID;
}
